/**
 * The SEAM onto `@vis.gl/react-google-maps` — the one module in the app that is
 * allowed to name that package's marker primitives, and the reason the app no
 * longer speaks its anchor convention.
 *
 * ── THE CLASS THIS CLOSES ───────────────────────────────────────────────────
 *
 * `AdvancedMarker` forwards `anchorLeft` / `anchorTop` verbatim to
 * `google.maps.marker.AdvancedMarkerElement`, where they are an OFFSET FROM THE
 * CONTENT'S TOP-LEFT CORNER whose defaults are `-50%` / `-100%` — i.e. the sign
 * is inverted relative to every "anchor point" convention a reader brings with
 * them. (The library's own compatibility shim for the deprecated `anchorPoint`
 * prop proves it: `useAdvancedMarkerAnchoring` converts `['50%','100%']` into
 * `anchorLeft = calc(-1 * 50%)`.) The listings map wrote `anchorLeft="50%"`
 * meaning "centre", got "one full card-width to the RIGHT", and shipped — the
 * card's centre measured 320 px off the pin's on production.
 *
 * A convention that reads backwards is not a thing to remember; it is a thing
 * to make unspellable. So the three anchor props are `Omit`ted from the
 * exported prop type here: **a marker's anchor is bottom-centre, always**, and
 * anything that needs another geometry is a POPOVER — a screen-space rectangle
 * constrained by a screen-space boundary — which is a different mechanism
 * (`map-popover.ts`, Floating UI) and not an anchor at all.
 *
 * The compile-time half is this `Omit`; the import half is the
 * `no-restricted-imports` row in `apps/public/eslint.config.mjs`, which refuses
 * `@vis.gl/react-google-maps` outside `src/shared/components/map/**`. Neither
 * alone is enough: the `Omit` cannot stop a call site importing the raw
 * component, and the lint row cannot stop THIS file from re-exporting the props.
 */
import {
  AdvancedMarker as VisAdvancedMarker,
  type AdvancedMarkerProps as VisAdvancedMarkerProps,
} from '@vis.gl/react-google-maps';

/**
 * The map pane and the SDK loader, re-exported UNCHANGED — neither has an
 * anchor convention to hide. They pass through the seam anyway so the
 * `no-restricted-imports` row can be TOTAL: a rule with an exception for "the
 * one harmless symbol" is a rule every future import argues with.
 *
 * ⚠ BILLABLE ON MOUNT. Rendering `APIProvider` injects the Maps JS script and
 * rendering `Map` is one Dynamic Maps load — so these two are re-exported for
 * exactly ONE importer, `MapBoundary.tsx`, which mounts them on VIEW (or on a
 * tap) instead of on mount. The `[map-boundary]` rows in
 * `apps/public/eslint.config.mjs` refuse them to every other file, this seam's
 * own directory included. Markers stay importable: a marker is only ever a
 * child of the boundary's `Map`.
 */
export { APIProvider, Map } from '@vis.gl/react-google-maps';
export type { MapCameraChangedEvent } from '@vis.gl/react-google-maps';

/**
 * The props this seam refuses to forward — the third-party anchor convention,
 * named once so the `Omit` below and the doc above cannot drift.
 *
 * `anchorPoint` is the DEPRECATED spelling of the same idea (with the opposite
 * sign again), so it goes too: leaving it would restore the class through the
 * door the library itself marked as legacy.
 */
type MapAnchorProp = 'anchorLeft' | 'anchorTop' | 'anchorPoint';

/** `AdvancedMarker`'s props, minus the anchor convention. */
export type AdvancedMarkerProps = Omit<VisAdvancedMarkerProps, MapAnchorProp>;

/**
 * A map-projected marker whose content hangs bottom-centre from its
 * coordinate — the library's own default, and now the only geometry a call
 * site can ask for.
 */
export function AdvancedMarker(props: AdvancedMarkerProps) {
  return <VisAdvancedMarker {...props} />;
}
